import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, lineHeights, fontWeights
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import DateUtils from 'utils/Date';
import {
    Button, Grid, Divider, Text, Image
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import Calendar from 'components/Calendar/Calendar';
import useChangeDateModal from 'components/GlobalModals/ChangeDateModal/hooks/useChangeDateModal';

const MODAL_BODY_MAX_HEIGHT = '404px'; // according to the design
const AUTO_DISMISS_DELAY = 2000; // 2 seconds

/**
 * ChangeDateModal - Modal component for changing auto-replenishment subscription delivery date.
 * Displays a calendar allowing users to select a new shipment date from available dates
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onDismiss - Callback fired when modal is dismissed/cancelled
 * @param {Function} props.onSubmit - Callback fired when user confirms date selection
 * @param {Date} props.initialSelectedDate - Initial selected date (typically current subscription next run date)
 * @returns {JSX.Element} ChangeDateModal component
 */
const ChangeDateModal = ({
    initialSelectedDate, isDateChangeSuccessful, isOpen, onDismiss, onSubmit
}) => {
    const {
        calendar, changeSelectedDate, localization, nextShipmentDate, selectedDate
    } = useChangeDateModal({
        initialSelectedDate
    });

    /**
     * Auto-dismiss modal after 2 seconds when date change is successful
     */
    useEffect(() => {
        if (!isDateChangeSuccessful) {
            return undefined;
        }

        const timeoutId = setTimeout(() => onDismiss(), AUTO_DISMISS_DELAY);

        return () => clearTimeout(timeoutId);
    }, [isDateChangeSuccessful, onDismiss]);

    /**
     * Handles submission of the selected date
     * Calls the onSubmit callback with the currently selected date
     */
    const handleSubmit = useCallback(() => {
        if (isDateChangeSuccessful) {
            return;
        }

        const selectedQueryDate = DateUtils.formatQueryDate(selectedDate);

        if (selectedQueryDate === DateUtils.formatQueryDate(initialSelectedDate)) {
            onDismiss();

            return;
        }

        onSubmit?.({ selectedDate: selectedQueryDate });
    }, [isDateChangeSuccessful, onSubmit, selectedDate, initialSelectedDate]);

    return (
        <Modal
            focusDialog={true}
            isDrawer={true}
            isOpen={isOpen}
            onDismiss={onDismiss}
            width={0}
        >
            <Modal.Header>
                <Modal.Title>{localization.modalTitle}</Modal.Title>
            </Modal.Header>

            <Text
                is='p'
                css={styles.nextShipment}
            >
                {localization.nextShipment} <Text css={styles.nextShipmentDate}>{nextShipmentDate}</Text>
            </Text>
            <Divider />
            <Grid css={styles.weekdayHeader}>
                {localization.weekDays.map(day => (
                    <Text key={day}>{day}</Text>
                ))}
            </Grid>
            <Divider />

            <Modal.Body
                paddingX={space[4]}
                css={styles.body}
            >
                <Calendar
                    maxMonths={calendar.maxMonths}
                    selectedDay={selectedDate}
                    availableDays={calendar.availableDays}
                    closedCalendarDates={[]}
                    onDayClick={changeSelectedDate}
                    isVertical={true}
                    isSmallView={true}
                    isV2BookingFlow={true}
                />
            </Modal.Body>
            <Modal.Footer
                hasBorder={true}
                hasShadowBorderForSMUI={true}
                paddingX={4}
            >
                <Grid css={styles.footerActions}>
                    <Button
                        variant='secondary'
                        onClick={onDismiss}
                        data-testid='cancelButton'
                        children={localization.cancelButton}
                    />
                    <Button
                        variant='primary'
                        onClick={handleSubmit}
                        data-testid='submitButton'
                    >
                        {isDateChangeSuccessful ? (
                            <>
                                <Image
                                    src='/img/ufe/success-login.svg'
                                    width={20}
                                    height={20}
                                    disableLazyLoad={true}
                                    css={styles.submitButtonIcon}
                                />
                                {localization.submitButtonSuccess}
                            </>
                        ) : (
                            localization.submitButton
                        )}
                    </Button>
                </Grid>
            </Modal.Footer>
        </Modal>
    );
};

const styles = {
    body: {
        maxHeight: MODAL_BODY_MAX_HEIGHT,
        paddingTop: 0,
        paddingBottom: `${space[4]}px`,
        overflowX: 'hidden',
        '& h3': {
            textTransform: 'capitalize'
        }
    },

    nextShipment: {
        lineHeight: lineHeights.tight,
        padding: `${space[4]}px`
    },
    nextShipmentDate: {
        fontWeight: fontWeights.bold,
        color: colors.green
    },

    weekdayHeader: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0,
        textAlign: 'center',
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.tight,
        textTransform: 'uppercase',
        padding: `${space[4]}px`
    },

    footerActions: {
        gap: `${space[4]}px`,
        gridTemplateColumns: ['1fr 1fr']
    },
    submitButtonIcon: {
        marginRight: `${space[2]}px`
    }
};

ChangeDateModal.propTypes = {
    initialSelectedDate: PropTypes.instanceOf(Date).isRequired,
    isDateChangeSuccessful: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(ChangeDateModal, 'ChangeDateModal');
